import React from "react";
import {Routes, Route, Navigate} from "react-router-dom";
import {useAuth} from "hooks/useAuth";
import {ThemeProvider} from "@mui/material";
import {MathJaxContext} from "better-react-mathjax";

import "App.css";
import CssBaseline from "@mui/material/CssBaseline";
import 'simplebar-react/dist/simplebar.min.css';
import "utils/icons";

//Theme
import themeLight from "themes/light";
import themeDark from "themes/dark";

//Modals
import Loading from "shared/ui/Loading";
import ErrorModal from "shared/ui/ErrorModal";
import ConfirmModal from "shared/ui/ConfirmModal";
import MediaModal from "./shared/textEditor/MediaModal";

//Layout
import LayoutProtected from "app/layout/Protected";
import LayoutPublic from "app/layout/Public";
import LayoutProtectedNoSidebar from "app/layout/ProtectedNoSidebar";
import LayoutUnverified from "app/layout/Unverified";
import LayoutProtectedSwap from "app/layout/ProtectedSwap";
import PublicFullScreen from "./app/layout/PublicFullScreen";
import LayoutPublicNoSidebar from "./app/layout/PublicNoSidebar";

//Login
import LogoutPage from "features/login/pages/Logout";
import RegisterPage from "features/login/pages/Register";
import EmailVerify from "features/login/pages/EmailVerify";
import ForgotPassword from "features/login/pages/ForgotPassword";
import ForgotPasswordTeachers from "features/login/pages/ForgotPasswordTeachers";
import LoginTeachersPage from "./features/login/pages/LoginTeachers";
import LoginTestsPage from "features/login/pages/LoginTests";
import RegisterSchoolTeachersPage from "./features/login/pages/RegisterSchoolTeachers";

//Dashboard
import StatisticsPage from "features/dashboard/pages/Statistics";
import NotFoundPage from "features/assistance/pages/NotFound";

//Users
import TeachersPage from "features/users/pages/Teachers";
import TeacherPage from "features/users/pages/Teacher";
import StudentsPage from "features/users/pages/Students";
import StudentPage from "features/users/pages/Student";
import ClassroomsPage from "features/classrooms/pages/Classrooms";
import ClassroomPage from "features/classrooms/pages/Classroom";

//Tests
import TestsSchoolPage from "features/tests/listing/pages/TestsSchool";
import TestsLiteracyPage from "features/tests/listing/pages/TestsLiteracy";
import TestsDigitalLiteracyPage from "features/tests/listing/pages/TestsDigitalLiteracy";
import TestsTrainingPage from "features/tests/listing/pages/TestsTraining";
import TestsBacPage from "features/tests/listing/pages/TestsBac";
import TestsEvaluationPage from "features/tests/listing/pages/TestsEvaluation";
import TestFinishedClientPage from "features/tests/test/pages/TestFinishedClient";
import TestsCustomPage from "./features/tests/listing/pages/TestsCustom";
import CustomTestItems from "./features/tests/listing/pages/CustomTestItems";
import CustomTestItem from "features/tests/listing/pages/CustomTestItem";
import TestsNumericLiteracyPage from "./features/tests/listing/pages/TestsNumericLiteracy";
import TestsScientificLiteracyPage from "./features/tests/listing/pages/TestsScientificLiteracy";
import CreateTestMultipleClassroomsCustom from "./features/tests/create/pages/CreateTestMultipleClassroomsCustom";
import CreateTestMultipleClassrooms from "./features/tests/create/pages/CreateTestMultipleClassrooms";
import CreateTestSchool from "./features/tests/create/pages/CreateTestSchool";
import CreateTestBac from "./features/tests/create/pages/CreateTestBac";
import CreateTestEvaluation from "./features/tests/create/pages/CreateTestEvaluation";
import CreateTestTraining from "./features/tests/create/pages/CreateTestTraining";
import CreateTestLiteracy from "./features/tests/create/pages/CreateTestLiteracy";
import CreateTestDigitalLiteracy from "./features/tests/create/pages/CreateTestDigitalLiteracy";
import CreateTestNumericLiteracy from "./features/tests/create/pages/CreateTestNumericLiteracy";
import CreateTestScientificLiteracy from "./features/tests/create/pages/CreateTestScientificLiteracy";
import CreateTestCustom from "./features/tests/create/pages/CreateTestCustom";

//Reports
import StudentAnswersReportPage from "features/reports/answerReport/pages/StudentAnswersReport";
import ReportPage from "features/reports/testReport/pages/Report";
import SchoolReportsPage from "./features/reports/schoolReport/pages/SchoolReports";
import SchoolReportGeneratePage from "./features/reports/schoolReport/pages/SchoolReportGenerate";
import SchoolReportPage from "./features/reports/schoolReport/pages/SchoolReport";

//Test
import TestFinishedPage from "features/tests/test/pages/TestFinished";
import TestPage from "features/tests/test/pages/Test";
import TestClientPage from "features/tests/test/pages/TestClient";

//Settings
import ProfilePage from "features/userProfile/pages/Profile";
import AddressPage from "features/userProfile/pages/Address";
import AddressesPage from "features/userProfile/pages/Addresses";
import UsersImportPage from "features/imports/pages/UsersImport";
import ProfilesPage from "features/login/pages/Profiles";
import TestTagsPage from "./features/tests/tags/pages/TestTags";
import TestTagPage from "./features/tests/tags/pages/TestTag";

//Assistance
import FaqPage from "features/assistance/pages/Faq";
import MessagesPage from "features/notifications/pages/Messages";
import Feedback from "features/assistance/pages/Feedback";
import ContactPage from "features/assistance/pages/Contact";
import TutorialsPage from "./features/assistance/pages/Tutorials";
import ChooseAccountType from "./features/login/pages/ChooseAccountType";
import LoginSchools from "./features/login/pages/LoginSchools";
import PNRR from "./features/publicPages/pages/PNRR";
import PNRAS from "./features/publicPages/pages/PNRAS";
import AccountType from "./features/publicPages/pages/AccountType";
import AboutBrioSchools from "./features/publicPages/pages/AboutBrioSchools";
import AboutBrioTeachers from "./features/publicPages/pages/AboutBrioTeachers";
import Subscriptions from "./features/publicPages/pages/Subscriptions";
import Instructions from "./features/publicPages/pages/Instructions";
import ScrollToTop from "./shared/ui/ScrollToTop";
import WorkPlanGenerate from "./features/workPlan/pages/WorkPlanGenerate";
import WorkPlans from "./features/workPlan/pages/WorkPlans";
import WorkPlan from "./features/workPlan/pages/WorkPlan";
import StudentWorkPlanPage from "./features/workPlan/pages/StudentWorkPlanPage";
import CustomSubcategories from "./features/tests/listing/pages/CustomSubcategories";
import CustomCategory from "./features/tests/listing/pages/CustomSubcategory";
import GenerateClassroomLogs from "./features/reports/classroomReport/pages/GenerateClassroomLogs";
import ClassroomsReports from "./features/reports/classroomReport/pages/ClassroomsReports";
import AnswersImport from "./features/imports/pages/AnswersImport";
import TestActivityReport from "./features/reports/activityReport/pages/TestActivityReport";
import EmailVerifyFromProfile from "./features/login/pages/EmailVerifyFromProfile";
import LayoutUniversal from "./app/layout/Universal";
import MaintenancePage from "./features/assistance/pages/Maintenance";
import ClassroomReport from "./features/reports/classroomReport/pages/ClassroomReport";

const config = {
    loader: {load: ["[tex]/html"]},
    tex: {
        packages: {"[+]": ["html"]},
        inlineMath: [
            ["$", "$"],
            ["\\(", "\\)"]
        ],
        displayMath: [
            ["$$", "$$"],
            ["\\[", "\\]"]
        ]
    }
};

const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === "true";

function App() {
    const {user, tenant, loading, error, confirm, media} = useAuth();

    const themeMode = React.useMemo(() => {
        return user?.theme === "dark" ? themeDark : themeLight;
    }, [user]);

    if (isMaintenanceMode) {
        return  (
            <ThemeProvider theme={themeMode}>
                <MaintenancePage />
            </ThemeProvider>
        );
    }

    return user === null ||
    !user.hasOwnProperty("theme") ||
    user.theme === "" ||
    tenant === null ||
    error === null ? null : (
        <MathJaxContext version={3} config={config}>
            <ThemeProvider theme={themeMode}>
                <CssBaseline enableColorScheme/>
                <div className="App">
                    <ScrollToTop />
                    <Routes>
                        <Route element={<LayoutPublic bgImage={"/images/brio-background.jpg"}/>}>
                            <Route path="/" element={<ChooseAccountType />} />
                        </Route>

                        <Route element={<LayoutPublic bgImage={"/images/brio-school-login-background.jpg"}/>}>
                            <Route path="/login/scoala" element={<LoginSchools/>}/>
                            <Route path="/login/teste" element={<LoginTestsPage/>}/>
                            <Route path="/inregistrare" element={<RegisterSchoolTeachersPage/>}/>
                            <Route path="/resetare-parola" element={<ForgotPassword/>}/>
                        </Route>

                        <Route element={<LayoutPublic bgImage={"/images/brio-teacher-login-background.jpg"}/>}>
                            <Route path="/login/profesori" element={<LoginTeachersPage/>}/>
                            <Route path="/inregistrare/profesori" element={<RegisterPage/>}/>
                            <Route path="/resetare-parola/profesori" element={<ForgotPasswordTeachers/>}/>
                        </Route>

                        <Route element={<LayoutPublic/>}>
                            <Route path="/pnrr" element={<PNRR />} />
                            <Route path="/pnras" element={<PNRAS />} />
                            <Route path="/tip-cont" element={<AccountType />} />
                            <Route path="/despre-cont-scoala" element={<AboutBrioSchools />} />
                            <Route path="/despre-cont-profesor" element={<AboutBrioTeachers />} />
                            <Route path="/abonamente" element={<Subscriptions />} />
                            <Route path="/instructiuni" element={<Instructions />} />
                            <Route path="/contact-brio" element={<ContactPage layoutPublic={true}/>}/>
                            {/*<Route path="/intrebari-frecvente-brio" element={<FaqPage/>}/>*/}
                        </Route>

                        <Route element={<PublicFullScreen/>}>
                            <Route path="/test">
                                <Route path=":id" element={<TestClientPage/>}/>
                            </Route>
                            {/*<Route path="/logout" element={<LogoutPage />} />*/}
                        </Route>

                        <Route element={<LayoutPublicNoSidebar/>}>
                            <Route path="teste/raport/:id" element={<ReportPage/>}/>
                            <Route path="teste/raspunsuri/:id" element={<StudentAnswersReportPage/>}/>
                            <Route path="teste/istoric-teste/raport/:id" element={<ReportPage/>}/>
                            <Route path="teste/istoric-teste/raspunsuri/:id" element={<StudentAnswersReportPage/>}/>

                        </Route>

                        <Route element={<LayoutUnverified/>}>
                            <Route path="/verifica-email" element={<EmailVerify/>}/>
                            <Route path="/login/profesori/verifica-email" element={<EmailVerify/>}/>
                        </Route>

                        <Route element={<LayoutProtectedSwap allowedRoles={["principal", "teacher"]}/>}>
                            <Route path="/profile" element={<ProfilesPage/>}/>
                        </Route>

                        <Route element={<LayoutProtected allowedRoles={["teacher"]}/>}>
                            <Route path="/teste/adauga-test" element={<CreateTestSchool/>}/>
                            <Route path="/teste-bac/adauga-test" element={<CreateTestBac/>}/>
                            <Route path="/teste-evaluare/adauga-test" element={<CreateTestEvaluation/>}/>
                            <Route path="/teste-antrenament/adauga-test" element={<CreateTestTraining/>}/>
                            <Route path="/teste-literatie/adauga-test" element={<CreateTestLiteracy/>}/>
                            <Route path="/teste-literatie-digitala/adauga-test" element={<CreateTestDigitalLiteracy/>}/>
                            <Route path="/teste-literatie-numerica/adauga-test" element={<CreateTestNumericLiteracy/>}/>
                            <Route path="/teste-proprii/adauga-test" element={<CreateTestCustom/>}/>
                            <Route path="/teste-literatie-stiintifica/adauga-test" element={<CreateTestScientificLiteracy/>}/>
                            <Route path="/clase/genereaza-plan-lucru" element={<WorkPlanGenerate/>}/>
                        </Route>

                        <Route element={<LayoutProtected allowedRoles={["teacher", "principal", "student"]}/>}>
                            <Route path="/plan-lucru">
                                <Route path="" element={(user.position === "teacher" || user.position === "principal") ? <WorkPlans/> : <StudentWorkPlanPage/>}/>
                            </Route>
                            <Route path="/statistici" element={<StatisticsPage/>}/>
                            <Route path="/intrebari-frecvente" element={<FaqPage/>}/>
                            <Route path="/tutoriale" element={<TutorialsPage/>}/>
                            <Route path="/feedback" element={<Feedback/>}/>
                            <Route path="/logout" element={<LogoutPage/>}/>
                            <Route path="/mesaje" element={<MessagesPage/>}/>
                            <Route path="/contact" element={<ContactPage layoutPublic={false}/>}/>
                            <Route path="/profil">
                                <Route path="" element={<ProfilePage/>}/>
                                <Route path=":action" element={<ProfilePage/>}/>
                            </Route>
                            <Route path="/teste">
                                <Route path="" element={<TestsSchoolPage/>}/>
                                <Route path="teste-finalizate" element={<TestsSchoolPage/>}/>
                                <Route path="istoric-teste" element={<TestsSchoolPage/>}/>
                            </Route>
                            <Route path="/teste-bac">
                                <Route path="" element={<TestsBacPage/>}/>
                                <Route path="teste-finalizate" element={<TestsBacPage/>}/>
                                <Route path="istoric-teste" element={<TestsBacPage/>}/>
                            </Route>
                            <Route path="/teste-evaluare">
                                <Route path="" element={<TestsEvaluationPage/>}/>
                                <Route path="teste-finalizate" element={<TestsEvaluationPage/>}/>
                                <Route path="istoric-teste" element={<TestsEvaluationPage/>}/>
                            </Route>
                            <Route path="teste-antrenament">
                                <Route path="" element={<TestsTrainingPage/>}/>
                                <Route path="teste-finalizate" element={<TestsTrainingPage/>}/>
                                <Route path="istoric-teste" element={<TestsTrainingPage/>}/>
                            </Route>
                            <Route path="teste-literatie">
                                <Route path="" element={<TestsLiteracyPage/>}/>
                                <Route path="teste-finalizate" element={<TestsLiteracyPage/>}/>
                                <Route path="istoric-teste" element={<TestsLiteracyPage/>}/>
                            </Route>
                            <Route path="teste-literatie-digitala">
                                <Route path="" element={<TestsDigitalLiteracyPage/>}/>
                                <Route path="teste-finalizate" element={<TestsDigitalLiteracyPage/>}/>
                                <Route path="istoric-teste" element={<TestsDigitalLiteracyPage/>}/>
                            </Route>
                            <Route path="teste-literatie-numerica">
                                <Route path="" element={<TestsNumericLiteracyPage/>}/>
                                <Route path="teste-finalizate" element={<TestsNumericLiteracyPage/>}/>
                                <Route path="istoric-teste" element={<TestsNumericLiteracyPage/>}/>
                            </Route>
                            <Route path="teste-literatie-stiintifica">
                                <Route path="" element={<TestsScientificLiteracyPage/>}/>
                                <Route path="teste-finalizate" element={<TestsScientificLiteracyPage/>}/>
                                <Route path="istoric-teste" element={<TestsScientificLiteracyPage/>}/>
                            </Route>
                            <Route path="teste-proprii">
                                <Route path="" element={<TestsCustomPage/>}/>
                                <Route path="teste-finalizate" element={<TestsCustomPage/>}/>
                                <Route path="istoric-teste" element={<TestsCustomPage/>}/>
                            </Route>
                        </Route>

                        <Route element={<LayoutProtected allowedRoles={["principal", "teacher"]}/>}>
                            <Route path="teste/log" element={<GenerateClassroomLogs/>}/>
                            <Route path="/plan-lucru">
                                <Route path=":id" element={<WorkPlan/>}/>
                                {/*<Route path=":id/:action" element={<StudentPage/>}/>*/}
                                <Route path="adauga" element={<WorkPlanGenerate/>}/>
                            </Route>
                            <Route path="/elevi">
                                <Route path="" element={<StudentsPage/>}/>
                                <Route path="arhiva" element={<StudentsPage/>}/>
                                <Route path=":id" element={<StudentPage/>}/>
                                <Route path=":id/:action" element={<StudentPage/>}/>
                                <Route path="adauga" element={<StudentPage/>}/>
                            </Route>
                            <Route path="/clase">
                                <Route path="" element={<ClassroomsPage/>}/>
                                <Route path="arhiva" element={<ClassroomsPage/>}/>
                                <Route path=":id" element={<ClassroomPage/>}/>
                                <Route path=":id/:action" element={<ClassroomPage/>}/>
                                <Route path="adauga" element={<ClassroomPage/>}/>
                            </Route>
                            <Route path="itemi-proprii">
                                <Route path="" element={<CustomTestItems/>}/>
                                <Route path="arhiva" element={<CustomTestItems/>}/>
                                <Route path=":id/:action" element={<CustomTestItem/>}/>
                                <Route path=":id" element={<CustomTestItem/>}/>
                                <Route path="adauga" element={<CustomTestItem/>}/>
                            </Route>
                            <Route path="subcategorii-proprii">
                                <Route path="" element={<CustomSubcategories/>}/>
                                <Route path="arhiva" element={<CustomSubcategories/>}/>
                                <Route path=":id/:action" element={<CustomCategory/>}/>
                                <Route path=":id" element={<CustomCategory/>}/>
                                <Route path="adauga" element={<CustomCategory/>}/>
                            </Route>
                            <Route path="/rapoarte-clasa">
                                <Route path="" element={<ClassroomsReports/>}/>
                                <Route path="genereaza" element={<SchoolReportGeneratePage/>}/>
                            </Route>
                            <Route path="/rapoarte-scoala">
                                <Route path="" element={<SchoolReportsPage/>}/>
                                <Route path="genereaza" element={<SchoolReportGeneratePage/>}/>
                            </Route>
                        </Route>

                        <Route element={<LayoutProtectedNoSidebar allowedRoles={["principal", "teacher"]}/>}>
                            <Route path="/teste">
                                <Route path="log/:id" element={<TestActivityReport/>}/>
                            </Route>
                            <Route path="/rapoarte-scoala">
                                <Route path=":id" element={<SchoolReportPage type="school"/>}/>
                            </Route>
                            <Route path="/rapoarte-clasa">
                                <Route path=":id" element={<ClassroomReport />}/>
                            </Route>
                            <Route path="teste/previzualizare/:id" element={<StudentAnswersReportPage/>}/>
                        </Route>

                        <Route element={<LayoutProtected
                            allowedRoles={tenant.type === "teacher" ? ["principal", "teacher"] : ["principal"]}/>}>
                            <Route path="/adrese">
                                <Route path="" element={<AddressesPage/>}/>
                                <Route path=":id/:action" element={<AddressPage/>}/>
                                <Route path="adauga" element={<AddressPage/>}/>
                            </Route>
                            <Route path="/importa" element={<UsersImportPage/>}/>
                            <Route path="/clase">
                                <Route path="adauga" element={<ClassroomPage/>}/>
                            </Route>
                            <Route path="/taguri">
                                <Route path="" element={<TestTagsPage/>}/>
                                <Route path="taguri-adaugate" element={<TestTagsPage/>}/>
                                <Route path="adauga" element={<TestTagPage/>}/>
                            </Route>
                        </Route>

                        <Route element={<LayoutProtected allowedRoles={["principal"]}/>}>
                            <Route path="/profesori">
                                <Route path="" element={<TeachersPage/>}/>
                                <Route path=":id/:action" element={<TeacherPage/>}/>
                                <Route path=":id" element={<TeacherPage/>}/>
                                <Route path="arhiva" element={<TeachersPage/>}/>
                                <Route path="adauga" element={<TeacherPage/>}/>
                            </Route>
                            <Route path="/generator-teste"  element={<CreateTestMultipleClassrooms/>}/>
                            <Route path="/generator-teste-proprii"  element={<CreateTestMultipleClassroomsCustom/>}/>
                            <Route path="/importa-raspunsuri" element={<AnswersImport/>}/>
                        </Route>

                        <Route element={<LayoutProtected fullScreen={true} allowedRoles={["student"]}/>}>
                            <Route path="/teste">
                                <Route path=":id" element={<TestPage/>}/>
                            </Route>
                        </Route>

                        <Route element={<LayoutProtected allowedRoles={["student"]}/>}>
                            <Route path="/teste/test-finalizat" element={<TestFinishedPage/>}/>
                            <Route path="/teste-bac/test-finalizat" element={<TestFinishedPage/>}/>
                            <Route path="/teste-evaluare/test-finalizat" element={<TestFinishedPage/>}/>
                            <Route path="/teste-antrenament/test-finalizat" element={<TestFinishedPage/>}/>
                            <Route path="teste-literatie/test-finalizat" element={<TestFinishedPage/>}/>
                            <Route path="/teste-literatie-digitala/test-finalizat" element={<TestFinishedPage/>}/>
                            <Route path="/teste-proprii/test-finalizat" element={<TestFinishedPage/>}/>
                            <Route path="/teste-literatie-numerica/test-finalizat" element={<TestFinishedPage/>}/>
                            <Route path="/teste-literatie-stiintifica/test-finalizat" element={<TestFinishedPage/>}/>
                        </Route>

                        <Route element={<LayoutUniversal/>}>
                            <Route path="notificari/verificare-email" element={<EmailVerifyFromProfile />}/>
                        </Route>

                        <Route path="*" element={<Navigate to="/"/>}/>
                        <Route path="/not-found" element={<NotFoundPage/>}/>
                        <Route path="/test/test-finalizat" element={<TestFinishedClientPage/>}/>
                    </Routes>

                    <Loading show={loading.show} modal={loading.modal}/>
                    <ErrorModal error={error}/>
                    <ConfirmModal confirm={confirm}/>
                    <MediaModal media={media}/>
                </div>
            </ThemeProvider>
        </MathJaxContext>
    );
}

export default App;
